// ShippingStep component for shipping configuration and logistics
// Handles shipping methods, zones, rates, and package settings
// Based on product-merchant-crud.html shipping section

import React, { useCallback, useState, useEffect } from "react";
import { WizardStepComponentProps } from "src/shared/forms/types/WizardTypes";
import { FormField } from "src/shared/forms/components";

// Utility function for class names
const classNames = (...classes: (string | undefined | false)[]): string => {
  return classes.filter(Boolean).join(" ");
};

// Package dimensions interface
interface PackageDimensions {
  length: number;
  width: number;
  height: number;
  weight: number;
}

// Shipping company configuration
interface ShippingCompany {
  id: string;
  name: string;
  icon: string;
  color: string;
  enabled: boolean;
  services: {
    id: string;
    name: string;
    description: string;
    enabled: boolean;
  }[];
}

// Pre-order settings
interface PreOrderSettings {
  enabled: boolean;
  processingDays?: number;
  type?: 'PRE_ORDER' | 'MADE_TO_ORDER' | 'CUSTOM';
}

export interface ShippingData {
  // Package Dimensions & Weight
  packageDimensions?: PackageDimensions;
  isDangerous?: boolean;

  // Packing Instructions
  packingInstructions?: string;

  // Shipping Companies & Services
  shippingCompanies?: ShippingCompany[];

  // Pre-Order Settings
  preOrderSettings?: PreOrderSettings;
}

// Shipping companies with their services
const SHIPPING_COMPANIES: ShippingCompany[] = [
  {
    id: "jne",
    name: "JNE - Jalur Nugraha Ekakurir",
    icon: "local_shipping",
    color: "red-600",
    enabled: true,
    services: [
      {
        id: "reg",
        name: "JNE Regular",
        description: "Standard delivery (3-5 days)",
        enabled: true,
      },
      {
        id: "yes",
        name: "JNE YES",
        description: "Express delivery (1-2 days)",
        enabled: false,
      },
      {
        id: "oke",
        name: "JNE OKE",
        description: "Economy delivery (2-4 days)",
        enabled: false,
      },
      {
        id: "cod",
        name: "JNE COD",
        description: "Cash on Delivery",
        enabled: false,
      },
    ],
  },
  {
    id: "jnt",
    name: "J&T Express",
    icon: "local_shipping",
    color: "red-500",
    enabled: false,
    services: [
      {
        id: "reg",
        name: "J&T Regular",
        description: "Standard delivery (3-5 days)",
        enabled: false,
      },
      {
        id: "express",
        name: "J&T Express",
        description: "Express delivery (1-2 days)",
        enabled: false,
      },
    ],
  },
  {
    id: "sicepat",
    name: "SiCepat",
    icon: "local_shipping",
    color: "blue-500",
    enabled: false,
    services: [
      {
        id: "reg",
        name: "SiCepat REG",
        description: "Regular delivery (3-5 days)",
        enabled: false,
      },
      {
        id: "halu",
        name: "SiCepat HALU",
        description: "Same day delivery",
        enabled: false,
      },
    ],
  },
  {
    id: "tiki",
    name: "TIKI",
    icon: "local_shipping",
    color: "yellow-600",
    enabled: false,
    services: [
      {
        id: "reg",
        name: "TIKI Regular",
        description: "Standard delivery (3-5 days)",
        enabled: false,
      },
      {
        id: "ons",
        name: "TIKI ONS",
        description: "Overnight service (1-2 days)",
        enabled: false,
      },
    ],
  },
  {
    id: "anteraja",
    name: "AnterAja",
    icon: "local_shipping",
    color: "red-500",
    enabled: false,
    services: [],
  },
  {
    id: "pos",
    name: "Pos Indonesia",
    icon: "local_shipping",
    color: "orange-600",
    enabled: false,
    services: [],
  },
  {
    id: "ninja",
    name: "Ninja Xpress",
    icon: "local_shipping",
    color: "black",
    enabled: false,
    services: [],
  },
];

const ShippingStep: React.FC<WizardStepComponentProps> = ({
  data,
  onDataChange,
  errors = [],
}) => {
  const shippingData = data as ShippingData;

  // Internal state for shipping companies - initialize from props or defaults
  const [shippingCompanies, setShippingCompanies] = useState<ShippingCompany[]>(
    () => {
      // If shipping data has companies, use those, otherwise use defaults
      return shippingData.shippingCompanies || SHIPPING_COMPANIES;
    }
  );

  // Internal state for expanded companies - start collapsed, expand only enabled companies
  const [expandedCompanies, setExpandedCompanies] = useState<string[]>([]);

  // Sync internal state with incoming props data
  useEffect(() => {
    if (shippingData.shippingCompanies) {
      setShippingCompanies(shippingData.shippingCompanies);
    }
  }, [shippingData.shippingCompanies]);

  // Auto-expand companies that are enabled when component mounts or data changes
  useEffect(() => {
    const enabledCompanyIds = shippingCompanies
      .filter((company) => company.enabled)
      .map((company) => company.id);

    if (enabledCompanyIds.length > 0) {
      setExpandedCompanies(enabledCompanyIds);
    }
  }, [shippingCompanies]);

  // Initialize default shipping data
  const getDefaultShippingData = useCallback(
    (): ShippingData => ({
      packageDimensions: {
        length: 0,
        width: 0,
        height: 0,
        weight: 0,
      },
      isDangerous: false,
      packingInstructions: "",
      shippingCompanies: SHIPPING_COMPANIES,
      preOrderSettings: {
        enabled: false,
        processingDays: undefined,
        type: 'PRE_ORDER',
      },
    }),
    []
  );

  // Merge current data with defaults
  const currentData = {
    ...getDefaultShippingData(),
    ...shippingData,
  };

  // Get error for specific field - similar to BasicInfoStep
  const getFieldError = (fieldName: string): string | undefined => {
    const fieldMap: { [key: string]: string[] } = {
      packageWeight: ["Package weight", "weight"],
      packageLength: ["Package length", "length"],
      packageWidth: ["Package width", "width"],
      packageHeight: ["Package height", "height"],
      shippingCompanies: ["shipping company", "shipping companies"],
      shippingServices: ["shipping service", "shipping services"],
      processingDays: ["Processing days", "processing days"],
    };

    const patterns = fieldMap[fieldName] || [fieldName];
    return errors.find((error) =>
      patterns.some((pattern) =>
        error.toLowerCase().includes(pattern.toLowerCase())
      )
    );
  };

  // Handle field changes
  const handleFieldChange = useCallback(
    (field: keyof ShippingData, value: any) => {
      const updatedData = {
        ...currentData,
        [field]: value,
      };
      onDataChange(updatedData);
    },
    [currentData, onDataChange]
  );

  // Handle package dimensions change
  const handleDimensionChange = useCallback(
    (field: keyof PackageDimensions, value: number) => {
      const updatedDimensions = {
        ...currentData.packageDimensions,
        [field]: value,
      };
      handleFieldChange("packageDimensions", updatedDimensions);
    },
    [currentData.packageDimensions, handleFieldChange]
  );

  // Handle shipping company toggle
  const handleShippingCompanyToggle = useCallback(
    (companyId: string) => {
      const updatedCompanies = shippingCompanies.map((company) => {
        if (company.id === companyId) {
          return { ...company, enabled: !company.enabled };
        }
        return company;
      });

      setShippingCompanies(updatedCompanies);
      handleFieldChange("shippingCompanies", updatedCompanies);

      // Also handle expanded state
      if (updatedCompanies.find((c) => c.id === companyId)?.enabled) {
        if (!expandedCompanies.includes(companyId)) {
          setExpandedCompanies([...expandedCompanies, companyId]);
        }
      } else {
        setExpandedCompanies(
          expandedCompanies.filter((id) => id !== companyId)
        );
      }
    },
    [shippingCompanies, expandedCompanies, handleFieldChange]
  );

  // Handle shipping service toggle
  const handleShippingServiceToggle = useCallback(
    (companyId: string, serviceId: string) => {
      const updatedCompanies = shippingCompanies.map((company) => {
        if (company.id === companyId) {
          return {
            ...company,
            services: company.services.map((service) => {
              if (service.id === serviceId) {
                return { ...service, enabled: !service.enabled };
              }
              return service;
            }),
          };
        }
        return company;
      });

      setShippingCompanies(updatedCompanies);
      handleFieldChange("shippingCompanies", updatedCompanies);
    },
    [shippingCompanies, handleFieldChange]
  );

  // Handle pre-order settings change
  const handlePreOrderChange = useCallback(
    (field: keyof PreOrderSettings, value: any) => {
      const updatedPreOrder = {
        ...currentData.preOrderSettings,
        [field]: value,
      };
      handleFieldChange("preOrderSettings", updatedPreOrder);
    },
    [currentData.preOrderSettings, handleFieldChange]
  );

  return (
    <div className="tw-space-y-4 md:tw-space-y-6">
      {/* Package Dimensions & Weight Group */}
      <div className="tw-bg-white dark:tw-bg-[#1C252E] tw-rounded-xl tw-border tw-border-gray-200 dark:tw-border-[#233648] tw-p-4 md:tw-p-6 tw-shadow-sm">
        <div className="tw-mb-6 tw-border-b tw-border-gray-200 dark:tw-border-[#233648] tw-pb-4">
          <h3 className="tw-text-lg tw-font-bold tw-text-gray-900 dark:tw-text-white">
            Package Dimensions & Weight
          </h3>
          <p className="tw-text-sm tw-text-gray-500 dark:tw-text-[#92adc9]">
            Provide accurate package measurements for shipping calculations.
          </p>
        </div>
        <div className="tw-grid tw-gap-6">
          {/* Package Weight */}
          <FormField
            label="Package Weight"
            name="packageWeight"
            required
            error={getFieldError("packageWeight")}
          >
            <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
              <div className="tw-relative">
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  placeholder="1.5"
                  value={currentData.packageDimensions?.weight || ""}
                  onChange={(e) =>
                    handleDimensionChange(
                      "weight",
                      parseFloat(e.target.value) || 0
                    )
                  }
                  className={`tw-w-full tw-bg-transparent tw-border tw-rounded-lg tw-px-4 tw-py-2.5 tw-pr-12 tw-text-gray-900 dark:tw-text-white tw-placeholder-gray-400 dark:tw-placeholder-gray-500 focus:tw-outline-none focus:tw-ring-2 focus:tw-border-transparent tw-transition-all ${
                    getFieldError("packageWeight")
                      ? "tw-border-red-300 dark:tw-border-red-600 focus:tw-ring-red-500"
                      : "tw-border-gray-300 dark:tw-border-[#364152] focus:tw-ring-primary"
                  }`}
                />
                <div className="tw-absolute tw-inset-y-0 tw-right-0 tw-flex tw-items-center tw-pr-3 tw-text-sm tw-text-gray-500 dark:tw-text-gray-400">
                  kg
                </div>
              </div>
              <div className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-text-gray-500 dark:tw-text-gray-400">
                <span className="material-symbols-outlined tw-text-blue-600">
                  info
                </span>
                <span>Include packaging weight for accurate shipping cost</span>
              </div>
            </div>
            {getFieldError("packageWeight") && (
              <p className="tw-text-xs tw-text-red-600 dark:tw-text-red-400">
                {getFieldError("packageWeight")}
              </p>
            )}
            <p className="tw-text-xs tw-text-gray-500 dark:tw-text-gray-400">
              Total weight including product and packaging materials.
            </p>
          </FormField>

          {/* Package Dimensions */}
          <FormField
            label="Package Dimensions"
            name="packageDimensions"
            required
            error={getFieldError("packageDimensions")}
          >
            <div className="tw-grid tw-grid-cols-3 tw-gap-4">
              <div>
                <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-600 dark:tw-text-gray-300 tw-mb-1">
                  Length (cm)
                </label>
                <input
                  type="number"
                  min="0"
                  placeholder="30"
                  value={currentData.packageDimensions?.length || ""}
                  onChange={(e) =>
                    handleDimensionChange(
                      "length",
                      parseFloat(e.target.value) || 0
                    )
                  }
                  className={`tw-w-full tw-bg-transparent tw-border tw-rounded-lg tw-px-4 tw-py-3 tw-min-h-[44px] tw-text-gray-900 dark:tw-text-white tw-placeholder-gray-400 dark:tw-placeholder-gray-500 focus:tw-outline-none focus:tw-ring-2 focus:tw-border-transparent tw-transition-all ${
                    getFieldError("packageLength")
                      ? "tw-border-red-300 dark:tw-border-red-600 focus:tw-ring-red-500"
                      : "tw-border-gray-300 dark:tw-border-[#364152] focus:tw-ring-primary"
                  }`}
                />
              </div>
              <div>
                <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-600 dark:tw-text-gray-300 tw-mb-1">
                  Width (cm)
                </label>
                <input
                  type="number"
                  min="0"
                  placeholder="20"
                  value={currentData.packageDimensions?.width || ""}
                  onChange={(e) =>
                    handleDimensionChange(
                      "width",
                      parseFloat(e.target.value) || 0
                    )
                  }
                  className={`tw-w-full tw-bg-transparent tw-border tw-rounded-lg tw-px-4 tw-py-3 tw-min-h-[44px] tw-text-gray-900 dark:tw-text-white tw-placeholder-gray-400 dark:tw-placeholder-gray-500 focus:tw-outline-none focus:tw-ring-2 focus:tw-border-transparent tw-transition-all ${
                    getFieldError("packageWidth")
                      ? "tw-border-red-300 dark:tw-border-red-600 focus:tw-ring-red-500"
                      : "tw-border-gray-300 dark:tw-border-[#364152] focus:tw-ring-primary"
                  }`}
                />
              </div>
              <div>
                <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-600 dark:tw-text-gray-300 tw-mb-1">
                  Height (cm)
                </label>
                <input
                  type="number"
                  min="0"
                  placeholder="10"
                  value={currentData.packageDimensions?.height || ""}
                  onChange={(e) =>
                    handleDimensionChange(
                      "height",
                      parseFloat(e.target.value) || 0
                    )
                  }
                  className={`tw-w-full tw-bg-transparent tw-border tw-rounded-lg tw-px-4 tw-py-3 tw-min-h-[44px] tw-text-gray-900 dark:tw-text-white tw-placeholder-gray-400 dark:tw-placeholder-gray-500 focus:tw-outline-none focus:tw-ring-2 focus:tw-border-transparent tw-transition-all ${
                    getFieldError("packageHeight")
                      ? "tw-border-red-300 dark:tw-border-red-600 focus:tw-ring-red-500"
                      : "tw-border-gray-300 dark:tw-border-[#364152] focus:tw-ring-primary"
                  }`}
                />
              </div>
            </div>
            {(getFieldError("packageLength") ||
              getFieldError("packageWidth") ||
              getFieldError("packageHeight")) && (
              <div className="tw-text-xs tw-text-red-600 dark:tw-text-red-400 tw-space-y-1">
                {getFieldError("packageLength") && (
                  <p>• {getFieldError("packageLength")}</p>
                )}
                {getFieldError("packageWidth") && (
                  <p>• {getFieldError("packageWidth")}</p>
                )}
                {getFieldError("packageHeight") && (
                  <p>• {getFieldError("packageHeight")}</p>
                )}
              </div>
            )}
            <p className="tw-text-xs tw-text-gray-500 dark:tw-text-gray-400">
              Outer package dimensions for shipping calculations.
            </p>
          </FormField>

          {/* Dangerous Item */}
          <div className="tw-flex tw-flex-col tw-gap-4">
            <label className="tw-text-sm tw-font-medium tw-text-gray-700 dark:tw-text-gray-200">
              Special Handling
            </label>
            <div className="tw-flex tw-items-center tw-justify-between tw-p-4 tw-bg-gray-50 dark:tw-bg-[#151f28] tw-rounded-lg tw-border tw-border-gray-200 dark:tw-border-[#364152]">
              <div className="tw-flex tw-items-center tw-gap-3">
                <span className="material-symbols-outlined tw-text-orange-600">
                  warning
                </span>
                <div>
                  <span className="tw-text-sm tw-font-medium tw-text-gray-900 dark:tw-text-white">
                    Is Dangerous Item?
                  </span>
                  <p className="tw-text-xs tw-text-gray-500 dark:tw-text-gray-400">
                    Requires special shipping handling (fragile, hazmat,
                    oversized, etc.)
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() =>
                  handleFieldChange("isDangerous", !currentData.isDangerous)
                }
                className={classNames(
                  "tw-relative tw-inline-flex tw-h-6 tw-w-11 tw-flex-shrink-0 tw-cursor-pointer tw-rounded-full tw-border-2 tw-border-transparent tw-transition-colors tw-duration-200 tw-ease-in-out focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-primary focus:tw-ring-offset-2 focus:tw-ring-offset-white dark:focus:tw-ring-offset-gray-800",
                  currentData.isDangerous
                    ? "tw-bg-primary"
                    : "tw-bg-gray-200 dark:tw-bg-gray-700"
                )}
                role="switch"
                aria-checked={currentData.isDangerous || false}
              >
                <span
                  className={classNames(
                    "tw-pointer-events-none tw-inline-block tw-h-5 tw-w-5 tw-rounded-full tw-bg-white tw-shadow tw-ring-0 tw-transition tw-duration-200 tw-ease-in-out",
                    currentData.isDangerous
                      ? "tw-translate-x-5"
                      : "tw-translate-x-0"
                  )}
                />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Packing Instructions Group */}
      <div className="tw-bg-white dark:tw-bg-[#1C252E] tw-rounded-xl tw-border tw-border-gray-200 dark:tw-border-[#233648] tw-p-4 md:tw-p-6 tw-shadow-sm">
        <div className="tw-mb-6 tw-border-b tw-border-gray-200 dark:tw-border-[#233648] tw-pb-4">
          <h3 className="tw-text-lg tw-font-bold tw-text-gray-900 dark:tw-text-white">
            Packing Instructions
          </h3>
          <p className="tw-text-sm tw-text-gray-500 dark:tw-text-[#92adc9]">
            Special instructions for packaging this product.
          </p>
        </div>
        <div className="tw-grid tw-gap-6">
          {/* Packing Instruction */}
          <div className="tw-flex tw-flex-col tw-gap-2">
            <label className="tw-text-sm tw-font-medium tw-text-gray-700 dark:tw-text-gray-200">
              Packing Instructions
            </label>
            <textarea
              value={currentData.packingInstructions || ""}
              onChange={(e) =>
                handleFieldChange("packingInstructions", e.target.value)
              }
              className="tw-w-full tw-bg-transparent tw-border tw-border-gray-300 dark:tw-border-[#364152] tw-rounded-lg tw-p-4 tw-text-gray-900 dark:tw-text-white tw-placeholder-gray-400 dark:tw-placeholder-gray-500 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-primary focus:tw-border-transparent tw-transition-all tw-min-h-[100px] tw-resize-none"
              placeholder="Special instructions for handling, packing, or shipping this product..."
            />
            <p className="tw-text-xs tw-text-gray-500 dark:tw-text-gray-400">
              Provide any special instructions for warehouse staff on how to
              handle and pack this product.
            </p>
          </div>
        </div>
      </div>

      {/* Shipping Companies & Services Group */}
      <div className="tw-bg-white dark:tw-bg-[#1C252E] tw-rounded-xl tw-border tw-border-gray-200 dark:tw-border-[#233648] tw-p-4 md:tw-p-6 tw-shadow-sm">
        <div className="tw-mb-6 tw-border-b tw-border-gray-200 dark:tw-border-[#233648] tw-pb-4">
          <h3 className="tw-text-lg tw-font-bold tw-text-gray-900 dark:tw-text-white">
            Shipping Companies & Services
          </h3>
          <p className="tw-text-sm tw-text-gray-500 dark:tw-text-[#92adc9]">
            Configure available shipping options for your customers.
          </p>
        </div>
        <div className="tw-grid tw-gap-6">
          {/* Shipping Companies with Services */}
          <FormField
            label="Available Shipping Companies & Services"
            name="shippingCompanies"
            required
            error={getFieldError("shippingCompanies")}
          >
            <p className="tw-text-xs tw-text-gray-500 dark:tw-text-gray-400">
              Select shipping companies and their specific services to give
              customers more delivery options.
            </p>

            {(getFieldError("shippingCompanies") ||
              getFieldError("shippingServices")) && (
              <div className="tw-text-xs tw-text-red-600 dark:tw-text-red-400 tw-space-y-1">
                {getFieldError("shippingCompanies") && (
                  <p>• {getFieldError("shippingCompanies")}</p>
                )}
                {getFieldError("shippingServices") && (
                  <p>• {getFieldError("shippingServices")}</p>
                )}
              </div>
            )}

            <div className="tw-space-y-4">
              {shippingCompanies.map((company) => (
                <div
                  key={company.id}
                  className="tw-border tw-border-gray-200 dark:tw-border-[#364152] tw-rounded-lg tw-overflow-hidden"
                >
                  <label
                    className="tw-flex tw-items-center tw-gap-3 tw-p-4 tw-cursor-pointer hover:tw-bg-gray-50 dark:hover:tw-bg-[#1a2531] tw-transition-all"
                    onClick={() => {
                      handleShippingCompanyToggle(company.id);
                    }}
                  >
                    <div
                      className={classNames(
                        "tw-w-5 tw-h-5 tw-border-2 tw-border-gray-300 dark:tw-border-gray-500 tw-rounded tw-flex tw-items-center tw-justify-center tw-transition-all",
                        company.enabled ? "tw-border-primary tw-bg-primary" : ""
                      )}
                    >
                      <span
                        className={classNames(
                          "material-symbols-outlined tw-text-white tw-text-sm tw-transition-opacity",
                          company.enabled ? "tw-opacity-100" : "tw-opacity-0"
                        )}
                      >
                        check
                      </span>
                    </div>
                    <div className="tw-flex tw-items-center tw-gap-2 tw-flex-1">
                      <span
                        className={`material-symbols-outlined tw-text-${company.color}`}
                      >
                        {company.icon}
                      </span>
                      <span className="tw-text-sm tw-font-medium tw-text-gray-700 dark:tw-text-gray-200">
                        {company.name}
                      </span>
                    </div>
                    <span
                      className={classNames(
                        "material-symbols-outlined tw-text-gray-400 tw-transition-transform",
                        expandedCompanies.includes(company.id)
                          ? "tw-rotate-180"
                          : ""
                      )}
                    >
                      expand_more
                    </span>
                  </label>

                  {expandedCompanies.includes(company.id) &&
                    company.services.length > 0 && (
                      <div className="tw-border-t tw-border-gray-200 dark:tw-border-[#364152] tw-bg-gray-50/50 dark:tw-bg-[#151f28]/50 tw-p-4">
                        <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-3">
                          {company.services.map((service) => (
                            <label
                              key={service.id}
                              className="tw-flex tw-items-center tw-gap-3 tw-p-3 tw-border tw-border-gray-200 dark:tw-border-[#364152] tw-rounded-lg tw-cursor-pointer hover:tw-border-primary hover:tw-bg-primary/5 tw-transition-all tw-group"
                            >
                              <input
                                type="checkbox"
                                className="tw-sr-only tw-peer"
                                checked={service.enabled}
                                onChange={() => handleShippingServiceToggle(company.id, service.id)}
                              />
                              <div
                                className={classNames(
                                  "tw-w-4 tw-h-4 tw-border-2 tw-border-gray-300 dark:tw-border-gray-500 tw-rounded tw-flex tw-items-center tw-justify-center tw-transition-all",
                                  service.enabled
                                    ? "tw-border-primary tw-bg-primary"
                                    : ""
                                )}
                              >
                                <span
                                  className={classNames(
                                    "material-symbols-outlined tw-text-white tw-text-xs tw-transition-opacity",
                                    service.enabled
                                      ? "tw-opacity-100"
                                      : "tw-opacity-0"
                                  )}
                                >
                                  check
                                </span>
                              </div>
                              <div className="tw-flex-1">
                                <span className="tw-text-sm tw-font-medium tw-text-gray-700 dark:tw-text-gray-200">
                                  {service.name}
                                </span>
                                <p className="tw-text-xs tw-text-gray-500 dark:tw-text-gray-400">
                                  {service.description}
                                </p>
                              </div>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}
                </div>
              ))}
            </div>
          </FormField>
        </div>
      </div>

      {/* Pre-Order Settings Group */}
      <div className="tw-bg-white dark:tw-bg-[#1C252E] tw-rounded-xl tw-border tw-border-gray-200 dark:tw-border-[#233648] tw-p-4 md:tw-p-6 tw-shadow-sm">
        <div className="tw-mb-6 tw-border-b tw-border-gray-200 dark:tw-border-[#233648] tw-pb-4">
          <div className="tw-flex tw-items-center tw-gap-2">
            <h3 className="tw-text-lg tw-font-bold tw-text-gray-900 dark:tw-text-white">
              Pre-Order Settings
            </h3>
          </div>
          <p className="tw-text-sm tw-text-gray-500 dark:tw-text-[#92adc9]">
            Configure pre-order options for products not immediately available
            for shipping.
          </p>
        </div>
        <div className="tw-grid tw-gap-6">
          {/* Enable Pre-Order */}
          <div className="tw-flex tw-items-center tw-justify-between tw-p-4 tw-bg-gray-50 dark:tw-bg-[#151f28] tw-rounded-lg tw-border tw-border-gray-200 dark:tw-border-[#364152]">
            <div className="tw-flex-1">
              <h4 className="tw-text-sm tw-font-medium tw-text-gray-900 dark:tw-text-white">
                Enable Pre-Order
              </h4>
              <p className="tw-text-xs tw-text-gray-500 dark:tw-text-gray-400 tw-mt-1">
                Allow customers to purchase before the product is ready to ship
              </p>
            </div>
            <button
              type="button"
              onClick={() =>
                handlePreOrderChange(
                  "enabled",
                  !currentData.preOrderSettings?.enabled
                )
              }
              className={classNames(
                "tw-relative tw-inline-flex tw-h-6 tw-w-11 tw-flex-shrink-0 tw-cursor-pointer tw-rounded-full tw-border-2 tw-border-transparent tw-transition-colors tw-duration-200 tw-ease-in-out focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-primary focus:tw-ring-offset-2 focus:tw-ring-offset-white dark:focus:tw-ring-offset-gray-800",
                currentData.preOrderSettings?.enabled
                  ? "tw-bg-primary"
                  : "tw-bg-gray-200 dark:tw-bg-gray-700"
              )}
              role="switch"
              aria-checked={currentData.preOrderSettings?.enabled || false}
            >
              <span
                className={classNames(
                  "tw-pointer-events-none tw-inline-block tw-h-5 tw-w-5 tw-rounded-full tw-bg-white tw-shadow tw-ring-0 tw-transition tw-duration-200 tw-ease-in-out",
                  currentData.preOrderSettings?.enabled
                    ? "tw-translate-x-5"
                    : "tw-translate-x-0"
                )}
              />
            </button>
          </div>

          {/* Processing Time (Conditional) */}
          {currentData.preOrderSettings?.enabled && (
            <FormField
              label="Processing Time"
              name="processingTime"
              required={currentData.preOrderSettings?.enabled}
              error={getFieldError("processingTime")}
            >
              <div className="tw-relative tw-max-w-xs">
                <input
                  type="number"
                  min="1"
                  max="365"
                  placeholder="7"
                  value={currentData.preOrderSettings?.processingDays || ""}
                  onChange={(e) =>
                    handlePreOrderChange(
                      "processingDays",
                      parseInt(e.target.value) || undefined
                    )
                  }
                  className={`tw-w-full tw-bg-transparent tw-border tw-rounded-lg tw-px-4 tw-py-2.5 tw-pr-12 tw-text-gray-900 dark:tw-text-white tw-placeholder-gray-400 dark:tw-placeholder-gray-500 focus:tw-outline-none focus:tw-ring-2 focus:tw-border-transparent tw-transition-all ${
                    getFieldError("processingDays")
                      ? "tw-border-red-300 dark:tw-border-red-600 focus:tw-ring-red-500"
                      : "tw-border-gray-300 dark:tw-border-[#364152] focus:tw-ring-primary"
                  }`}
                  required={currentData.preOrderSettings?.enabled}
                />
                <div className="tw-absolute tw-inset-y-0 tw-right-0 tw-flex tw-items-center tw-pr-3 tw-text-sm tw-text-gray-500 dark:tw-text-gray-400">
                  days
                </div>
              </div>
              {getFieldError("processingDays") && (
                <p className="tw-text-xs tw-text-red-600 dark:tw-text-red-400">
                  {getFieldError("processingDays")}
                </p>
              )}
              <p className="tw-text-xs tw-text-gray-500 dark:tw-text-gray-400">
                Number of days needed to prepare and process the order before
                shipping (1-365 days)
              </p>
            </FormField>
          )}

          {/* Pre-Order Type Selection (Conditional) */}
          {currentData.preOrderSettings?.enabled && (
            <FormField
              label="Pre-Order Type"
              name="preOrderType"
              required={currentData.preOrderSettings?.enabled}
              error={getFieldError("preOrderType")}
            >
              <select
                value={currentData.preOrderSettings?.type || 'PRE_ORDER'}
                onChange={(e) => handlePreOrderChange('type', e.target.value)}
                className={`tw-w-full tw-bg-transparent tw-border tw-rounded-lg tw-px-4 tw-py-3 tw-min-h-[44px] tw-text-gray-900 dark:tw-text-white focus:tw-outline-none focus:tw-ring-2 focus:tw-border-transparent tw-transition-all ${
                  getFieldError('preOrderType') 
                    ? 'tw-border-red-300 dark:tw-border-red-600 focus:tw-ring-red-500' 
                    : 'tw-border-gray-300 dark:tw-border-[#364152] focus:tw-ring-primary'
                }`}
                required={currentData.preOrderSettings?.enabled}
              >
                <option value="PRE_ORDER">Pre-Order - Customer pays now, ships when ready</option>
                <option value="MADE_TO_ORDER">Made to Order - Custom production after order placed</option>
                <option value="CUSTOM">Custom - Personalized/customized items</option>
              </select>
              {getFieldError('preOrderType') && (
                <p className="tw-text-xs tw-text-red-600 dark:tw-text-red-400">
                  {getFieldError('preOrderType')}
                </p>
              )}
              <p className="tw-text-xs tw-text-gray-500 dark:tw-text-gray-400">
                Select the type of pre-order handling for this product.
              </p>
            </FormField>
          )}
        </div>
      </div>

      {/* Step Errors Display */}
      {errors.length > 0 && (
        <div className="tw-bg-red-50 dark:tw-bg-red-900/20 tw-border tw-border-red-200 dark:tw-border-red-700 tw-rounded-lg tw-p-4">
          <div className="tw-flex tw-items-start tw-gap-3">
            <span className="material-symbols-outlined tw-text-red-600 dark:tw-text-red-400 tw-mt-0.5">
              error
            </span>
            <div>
              <h4 className="tw-font-medium tw-text-red-800 dark:tw-text-red-300 tw-mb-2">
                Please correct the following errors:
              </h4>
              <ul className="tw-text-sm tw-text-red-700 dark:tw-text-red-400 tw-space-y-1">
                {errors.map((error, index) => (
                  <li key={index}>• {error}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShippingStep;
